// Fixed set of carriage/stock options chosen per event (INFO ONLY). The stored
// value is the short `code`; `desc` and `seats` are display-only. Surfaced on
// the Sync Hub Ticketing card as a note for the person building the MERAC
// sessions — no downstream logic keys on these. The ORDER an event stores them
// in is its FORMATION, north (first) to south (last) — see the Carriages card
// on the Ticketing tab (2026-09-17). `seats` feeds the card's seats column and
// total (2026-09-18).
//
// ⚠️ The SEAT COUNTS ARE MIRRORED in the Sync Hub (sync-hub-nymr
// server/src/ticketing.ts CARRIAGE_SEATS) because dbo.EventCarriages stores
// codes only — when a carriage is added or its seating changes, update both.
import type { SeatSelection, CarriageTicketInput } from '../types'

export interface CarriageOption {
  code: string
  desc: string
  seats: number
}

export const CARRIAGE_OPTIONS: CarriageOption[] = [
  { code: 'TSO 64', desc: 'Open 64 Seats & Tables', seats: 64 },
  { code: 'TSO 60', desc: 'Open 60 Seats & Tables', seats: 60 },
  { code: 'BSO 39', desc: 'Brake 39 Seats & Tables', seats: 39 },
  { code: 'RMB 44', desc: 'Open Buffet 44 Seats & Tables', seats: 44 },
  { code: 'FussFree', desc: 'Open 48 Seats & Tables with 2 Wheelchair User Spaces', seats: 48 },
]

// Letter for a position in the formation, counted from the NORTH end: A, B …
// Z, AA, AB … Two carriages of the same type are told apart by their letters
// (A TSO 64, C TSO 64); re-ordering re-letters. The Sync Hub prints the same.
export function formationLetter(index: number): string {
  let n = index
  let s = ''
  do {
    s = String.fromCharCode(65 + (n % 26)) + s
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return s
}

// Seats per carriage code; undefined for a code no longer in the catalogue.
export function carriageSeats(code: string): number | undefined {
  return CARRIAGE_OPTIONS.find((c) => c.code === code)?.seats
}

// ---- ticket keys ------------------------------------------------------------
// A ticket on a carriage is EITHER a ticket-build type ('em') OR a MERAC ticket
// class already selling on the event ('merac', db/089). One string key covers
// both, so the Carriages card can tick, count and total them alike:
// "em:<eventTicketTypeId>" | "merac:<ticketClassMeracId>".
export type TicketKey = string
export const emTicketKey = (eventTicketTypeId: number): TicketKey => `em:${eventTicketTypeId}`
export const meracTicketKey = (ticketClassMeracId: string): TicketKey => `merac:${ticketClassMeracId}`
export function ticketKey(t: { eventTicketTypeId: number | null; ticketClassMeracId: string | null }): TicketKey {
  return t.ticketClassMeracId != null ? meracTicketKey(t.ticketClassMeracId) : emTicketKey(t.eventTicketTypeId ?? 0)
}
// A key + quantity back into the wire shape (PUT …/carriages/{carriageId}/tickets):
// exactly one id. Split on the FIRST colon so an id with a colon in it survives.
export function ticketInputFromKey(key: TicketKey, quantity: number): CarriageTicketInput {
  const at = key.indexOf(':')
  const source = key.slice(0, at)
  const id = key.slice(at + 1)
  return source === 'merac' ? { ticketClassMeracId: id, quantity } : { eventTicketTypeId: Number(id), quantity }
}

// The "Seats" pills on the Carriages card — a pair, one active once chosen.
export const SEAT_SELECTION_OPTIONS: { value: SeatSelection; label: string; hint: string }[] = [
  { value: 'none', label: 'No Seat Selection', hint: 'Build the MERAC sessions without seat selection' },
  { value: 'sets', label: 'Sets As Defined', hint: 'Build the MERAC sessions with the seat sets as defined by this formation' },
]
